import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  TextInput,
  useToast,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { api, describeError } from '../lib/api';
import { useResource } from '../lib/hooks';
import { formatDateTime } from '../lib/format';

interface Provider {
  id: string;
  key: string;
  type: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  _count: { accounts: number };
}

interface Adapter {
  type: string;
  displayName: string;
  requiresBaseUrl: boolean;
  defaultBaseUrl: string | null;
}

export function ProvidersPage({ role }: { role: string }) {
  const providers = useResource<{ items: Provider[] }>('/api/admin/providers');
  const adapters = useResource<{ items: Adapter[] }>('/api/admin/adapters');
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ key: '', type: 'openai', name: '', description: '' });
  const canWrite = role === 'OWNER' || role === 'ADMIN';

  const create = async () => {
    try {
      await api.post('/api/admin/providers', {
        key: form.key,
        type: form.type,
        name: form.name,
        ...(form.description ? { description: form.description } : {}),
      });
      toast.success('Dostawca utworzony', form.key);
      setOpen(false);
      setForm({ key: '', type: 'openai', name: '', description: '' });
      providers.reload();
    } catch (error) {
      const described = describeError(error);
      toast.error(described.title, described.body);
    }
  };

  return (
    <>
      <PageHeader
        title="Dostawcy"
        description="Typ adaptera decyduje o tym, jak Hub buduje żądanie i klasyfikuje odpowiedź. Poświadczenia należą do kont, nie do dostawcy."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setOpen(true)}>
              Dodaj dostawcę
            </Button>
          ) : undefined
        }
      />

      <Card title="Dostępne adaptery" description="Wbudowane implementacje ProviderAdapter." tight>
        <DataTable
          rows={adapters.data?.items ?? []}
          rowKey={(row) => row.type}
          columns={[
            {
              key: 'type',
              header: 'Typ',
              render: (row) => <span className="ah-mono">{row.type}</span>,
            },
            { key: 'name', header: 'Nazwa', render: (row) => row.displayName },
            {
              key: 'baseUrl',
              header: 'Domyślny adres',
              render: (row) => (
                <span className="ah-mono">{row.defaultBaseUrl ?? '— (wymagany własny)'}</span>
              ),
            },
          ]}
        />
      </Card>

      <Card title="Skonfigurowani dostawcy" tight>
        {providers.loading ? (
          <div className="ah-card-body">
            <Skeleton rows={4} />
          </div>
        ) : (
          <DataTable
            rows={providers.data?.items ?? []}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                title="Brak dostawców"
                description="Dodaj dostawcę, a następnie konto z poświadczeniami."
              />
            }
            columns={[
              {
                key: 'key',
                header: 'Klucz',
                render: (row) => <span className="ah-mono">{row.key}</span>,
              },
              {
                key: 'type',
                header: 'Adapter',
                render: (row) => <Badge tone="info">{row.type}</Badge>,
              },
              { key: 'name', header: 'Nazwa', render: (row) => row.name },
              {
                key: 'accounts',
                header: 'Konta',
                align: 'right',
                render: (row) => row._count.accounts,
              },
              {
                key: 'state',
                header: 'Stan',
                render: (row) => (
                  <Badge tone={row.isActive ? 'ok' : 'danger'}>
                    {row.isActive ? 'aktywny' : 'wyłączony'}
                  </Badge>
                ),
              },
              {
                key: 'created',
                header: 'Utworzono',
                render: (row) => formatDateTime(row.createdAt),
              },
              {
                key: 'actions',
                header: '',
                align: 'right',
                render: (row) =>
                  canWrite ? (
                    <Button
                      size="sm"
                      onClick={async () => {
                        try {
                          await api.patch(`/api/admin/providers/${row.id}`, {
                            isActive: !row.isActive,
                          });
                          providers.reload();
                        } catch (error) {
                          const described = describeError(error);
                          toast.error(described.title, described.body);
                        }
                      }}
                    >
                      {row.isActive ? 'Wyłącz' : 'Włącz'}
                    </Button>
                  ) : null,
              },
            ]}
          />
        )}
      </Card>

      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Nowy dostawca"
        description="Dostawca grupuje konta tego samego typu. Sam nie przechowuje poświadczeń."
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Anuluj</Button>
            <Button variant="primary" onClick={create} disabled={!form.key || !form.name}>
              Utwórz
            </Button>
          </>
        }
      >
        <Field
          label="Klucz"
          hint="Małe litery, cyfry, myślnik. Używany w logach i identyfikatorach."
        >
          {(id) => (
            <TextInput
              id={id}
              value={form.key}
              onChange={(event) => setForm({ ...form, key: event.target.value })}
              placeholder="openai"
            />
          )}
        </Field>
        <Field label="Typ adaptera">
          {(id) => (
            <Select
              id={id}
              value={form.type}
              onChange={(event) => setForm({ ...form, type: event.target.value })}
            >
              {(adapters.data?.items ?? []).map((adapter) => (
                <option key={adapter.type} value={adapter.type}>
                  {adapter.displayName}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Nazwa">
          {(id) => (
            <TextInput
              id={id}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          )}
        </Field>
        <Field label="Opis" hint="Opcjonalny.">
          {(id) => (
            <TextInput
              id={id}
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
          )}
        </Field>
      </Modal>
    </>
  );
}
